import { useContext, useEffect, useRef, useState } from 'react';
import sharkAttackImg from '../../../../assets/images/shark-attack-smartphone.webp';
import { FluidDropdown } from '../../../../components/ui/fluid-dropdown';
import { convertToArenaOptions, convertToCourtOptions } from '../../../../utils/index';
import { getRecentCourts, removeRecentCourt } from '../../../../utils/recentCourts';
import type { RecentCourt } from '../../../../utils/recentCourts';
import { SearchContext } from '../..';
import { useLocation } from 'react-router-dom';
import { Search as SearchIcon, Clock, MapPin, X } from 'lucide-react';
import { HoverButton } from '../../../../components/ui/hover-glow-button';

// data local de hoje no formato YYYY-MM-DD (compatível com <input type="date">)
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface SearchFormProps {
  dataArena: any;
  onSearch: () => void;
}

const citiesByState: Record<string, { id: string; value: string }[]> = {
  mg: [
    { id: 'belohorizonte', value: 'Belo Horizonte' },
    { id: 'diamantina', value: 'Diamantina' },
    { id: 'setelagoas', value: 'Sete Lagoas' },
    { id: 'perdoes', value: 'Perdões' },
    { id: 'itamarandiba', value: 'Itamarandiba' },
    { id: 'itaguara', value: 'Itaguara' },
    { id: 'santaluzia', value: 'Santa Luzia' },
  ],
  ba: [
    { id: 'Jequie', value: 'Jequié' },
  ],
};
const optionsState = [
  { id: 'mg', value: 'MG' },
  { id: 'ba', value: 'BA' },
];

function SearchForm({ onSearch, dataArena }: SearchFormProps) {
  const location = useLocation();
  const { city, setCity, state, setState, arena, setArena, court, setCourt, date, setDate } =
    useContext<any>(SearchContext);

  const [arenaOptions, setArenaOptions] = useState<any>();
  const [courtOptions, setCourtOptions] = useState();
  const [recentCourts, setRecentCourts] = useState<RecentCourt[]>([]);
  const skipCityReset = useRef(false);

  const optionsCity = state?.id ? citiesByState[state.id] || [] : [];

  useEffect(() => {
    setRecentCourts(getRecentCourts());
  }, []);

  // Clicar num atalho: preenche estado/cidade/arena/quadra e a data de hoje.
  const applyRecent = (r: RecentCourt) => {
    skipCityReset.current = true; // evita que o efeito abaixo limpe a cidade
    setState(r.state);
    setCity(r.city);
    setArena(r.arena);
    setCourt(r.court);
    setCourtOptions(convertToCourtOptions(r.arena));
    if (!date) setDate(todayLocal());
  };

  const handleRemoveRecent = (r: RecentCourt) => {
    removeRecentCourt(r);
    setRecentCourts(getRecentCourts());
  };

  useEffect(() => {
    if (dataArena) setArenaOptions(convertToArenaOptions(dataArena));
  }, [dataArena]);

  useEffect(() => {
    if (arena) setCourtOptions(convertToCourtOptions(arena));
  }, [arena]);

  useEffect(() => {
    // ao restaurar um atalho, não limpa a cidade que acabamos de setar
    if (skipCityReset.current) {
      skipCityReset.current = false;
      return;
    }
    setCity(undefined);
  }, [state]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const cityParam = searchParams.get('city');
    const stateParam = searchParams.get('state');
    const arenaParam = searchParams.get('arena');

    if (stateParam) {
      const matchedState = optionsState.find((option) => option.id === stateParam);
      setState(matchedState);
      if (cityParam && matchedState) {
        const stateCities = citiesByState[matchedState.id] || [];
        const matchedCity = stateCities.find((option) => option.id === cityParam);
        setCity(matchedCity);
      }
    }
    if (arenaParam && arenaOptions) {
      const matchedArena = arenaOptions.find((option: any) => option.value === arenaParam);
      setArena(matchedArena);
    }
  }, [location.search, arenaOptions]);

  const isDisabled = !(city && state && arena && court && date);

  return (
    <div className="min-h-screen flex flex-col justify-end overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-7xl w-full mx-auto flex flex-col md:flex-row items-end gap-8 lg:gap-16 px-4 md:px-8 flex-1">
        {/* Left: Image — aligned to bottom so hand doesn't crop */}
        <div className="hidden md:flex flex-shrink-0 items-end relative self-end" style={{ flex: '0 0 42%' }}>
          <div
            className="absolute inset-0 blur-3xl opacity-15"
            style={{ background: 'radial-gradient(circle at 50% 80%, #e57605, transparent 70%)' }}
          />
          <img
            src={sharkAttackImg}
            alt="Celular com vídeo de esporte"
            className="relative w-full max-w-[520px] object-contain drop-shadow-2xl"
          />
        </div>

        {/* Right: Form */}
        <div className="flex-1 w-full max-w-lg self-center pb-16 pt-24">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
            Seus Vídeos
          </h2>
          <p className="text-zinc-500 mb-8">Selecione a arena, quadra e data para encontrar seus vídeos.</p>

          {recentCourts.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-xs font-semibold tracking-wider text-orange-400 uppercase">
                  Últimas quadras
                </span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {recentCourts.map((r, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => applyRecent(r)}
                    className="group flex items-center gap-2 pl-3 pr-2 py-2 rounded-xl border border-zinc-700 bg-zinc-900 hover:border-orange-500/60 hover:bg-zinc-800/80 transition-colors"
                  >
                    <MapPin className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-zinc-100">{r.arena.value}</span>
                    <span className="text-xs text-zinc-500">· {r.court.value}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Remover"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveRecent(r);
                      }}
                      className="ml-1 grid place-items-center w-5 h-5 rounded-md text-zinc-600 hover:text-zinc-200 hover:bg-zinc-700/60"
                    >
                      <X className="w-3.5 h-3.5" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-zinc-400">Estado</label>
                <FluidDropdown
                  selectedOption={state}
                  onSelect={setState}
                  options={optionsState}
                  placeholder="Estado"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-zinc-400">Cidade</label>
                <FluidDropdown
                  selectedOption={city}
                  onSelect={setCity}
                  options={optionsCity}
                  placeholder="Cidade"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-zinc-400">Arena</label>
                <FluidDropdown
                  selectedOption={arena}
                  onSelect={setArena}
                  options={arenaOptions}
                  placeholder="Arena"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-zinc-400">Quadra</label>
                <FluidDropdown
                  selectedOption={court}
                  onSelect={setCourt}
                  options={courtOptions}
                  placeholder="Quadra"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-zinc-400">Data</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full h-10 px-3 rounded-lg text-sm font-medium border border-zinc-700 bg-zinc-900 text-zinc-300 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <HoverButton
              onClick={onSearch}
              disabled={isDisabled}
              className="w-full mt-6"
              glowColor="#f29b2a"
              backgroundColor={isDisabled ? '#3f3f46' : '#e57605'}
            >
              <SearchIcon className="w-4 h-4" />
              Buscar vídeos
            </HoverButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchForm;
